import React, { FormEvent } from 'react';

import { useFormik } from 'formik-next';
import styled from 'styled-components/macro';
import * as Yup from 'yup';

import { theme } from '@waglabs/base-theme';
import { Box } from '@waglabs/box-next';
import { Divider } from '@waglabs/divider';
import { Grid } from '@waglabs/grid';
import { SvgIconProps } from '@waglabs/icons';
import { Typography } from '@waglabs/typography';

import {
  DataTestIds,
  PetGender,
  PetStrings,
  petWeightOptions,
} from '../../../../../../../constants';
import {
  CheckConfirmedIcon,
  FemaleIcon,
  MaleIcon,
  PetWeightExtraLargeIcon,
  PetWeightLargeIcon,
  PetWeightMediumIcon,
  PetWeightSmallIcon,
} from '../../../../../../../icons';
import { StepFooter } from '../../StepFooter';

import { FieldLabel } from './FieldLabel';
import { FieldLegend } from './FieldLegend';
import { HiddenRadioInput } from './HiddenRadioInput';

const GenderIcon = {
  [PetGender.Male]: <MaleIcon />,
  [PetGender.Female]: <FemaleIcon />,
} as const;

const WeightIcon = {
  [PetStrings.Weight.Small]: <PetWeightSmallIcon
    height="40px"
    width="40px"
    justifySelf="center"
  />,
  [PetStrings.Weight.Medium]: <PetWeightMediumIcon
    height="40px"
    width="40px"
    justifySelf="center"
  />,
  [PetStrings.Weight.Large]: <PetWeightLargeIcon
    height="40px"
    width="40px"
    justifySelf="center"
  />,
  [PetStrings.Weight.XL]: <PetWeightExtraLargeIcon
    height="40px"
    width="40px"
    justifySelf="center"
  />,
} as const;

const PetSterilizeOptions = [{
  value: PetStrings.Sterile.Yes,
  label: 'Yes',
  datatestId: DataTestIds.Signup.PetInfoForm2.PetSterilizedYes,
}, {
  value: PetStrings.Sterile.No,
  label: 'No',
  datatestId: DataTestIds.Signup.PetInfoForm2.PetSterilizedNo,
}, {
  value: PetStrings.Sterile.Unspecified,
  label: 'Not sure',
  datatestId: DataTestIds.Signup.PetInfoForm2.PetSterilizedNotSure,
}] as const;

export const FormFields = {
  PetGender: 'petGender',
  PetSterilized: 'petSterilized',
  PetWeight: 'petWeight',
} as const;

export const schema = Yup.object().shape({
  [FormFields.PetGender]: Yup.string().oneOf([
    PetGender.Male,
    PetGender.Female,
  ]).required().label('Gender'),
  [FormFields.PetSterilized]: Yup.string().oneOf(
    PetSterilizeOptions.map((option) => option.value),
  ).required().label('Spayed/neutered'),
  [FormFields.PetWeight]: Yup.number().oneOf([
    PetStrings.Weight.Small,
    PetStrings.Weight.Medium,
    PetStrings.Weight.Large,
    PetStrings.Weight.XL,
  ]).required().label('Weight'),
});

export type PetInfoPart2FormProps = {
  formik: ReturnType<typeof useFormik>;
};
export const PetInfoPart2Form = ({
  formik,
}: PetInfoPart2FormProps) => (
  <Box
    as="form"
    onSubmit={(event) => {
      formik.handleSubmit(event as FormEvent<HTMLFormElement>);
    }}
  >
    <Grid
      as="fieldset"
      gridTemplateColumns="repeat(2, 1fr)"
      gridGap="8px"
      p={0}
      position="relative"
    >
      <FieldLegend
        as="legend"
        fontSize="16px"
        fontFamily="muliBold"
      >
        Gender:
      </FieldLegend>
      {Object.values(PetGender).map((gender) => (
        <Box
          key={gender}
          mt="30px"
        >
          <HiddenRadioInput
            type="radio"
            id={gender}
            name="gender"
            checked={formik.values[FormFields.PetGender] === gender}
            onChange={() => {
              formik.setFieldValue(FormFields.PetGender, gender);
            }}
            {...gender === PetGender.Male && {
              'data-testId': DataTestIds.Signup.PetInfoForm2.PetGenderMale,
            }}
            {...gender === PetGender.Female && {
              'data-testId': DataTestIds.Signup.PetInfoForm2.PetGenderFemale,
            }}
          />
          <FieldLabel
            htmlFor={gender}
            display="flex"
            height="50px"
            gridGap="16px"
            checked={formik.values[FormFields.PetGender] === gender}
          >
            {formik.values[FormFields.PetGender] === gender && (
              <StyledConfirmedCheckIcon />
            )}

            {GenderIcon[gender]}
            <Typography
              m={0}
              color="inherit"
            >
              {gender}
            </Typography>
          </FieldLabel>
        </Box>
      ))}
    </Grid>

    <Grid
      as="fieldset"
      gridTemplateColumns={['1fr', 'repeat(3, 1fr)']}
      gridGap="8px"
      p={0}
      position="relative"
    >
      <FieldLegend
        as="legend"
        fontSize="16px"
        fontFamily="muliBold"
      >
        Spayed/neutered:
      </FieldLegend>
      {PetSterilizeOptions.map((sterilizeOption) => (
        <Box
          key={sterilizeOption.value}
          mt="30px"
        >
          <HiddenRadioInput
            type="radio"
            id={sterilizeOption.value}
            name="sterilize"
            checked={formik.values[FormFields.PetSterilized] === sterilizeOption.value}
            onChange={() => {
              formik.setFieldValue(FormFields.PetSterilized, sterilizeOption.value);
            }}
            {...{
              'data-testId': sterilizeOption.datatestId,
            }}
          />
          <FieldLabel
            htmlFor={sterilizeOption.value}
            display="flex"
            height="50px"
            checked={formik.values[FormFields.PetSterilized] === sterilizeOption.value}
          >
            {formik.values[FormFields.PetSterilized] === sterilizeOption.value && (
              <StyledConfirmedCheckIcon />
            )}
            <Typography
              m={0}
              color="inherit"
            >
              {sterilizeOption.label}
            </Typography>
          </FieldLabel>
        </Box>
      ))}
    </Grid>

    <Grid
      as="fieldset"
      gridTemplateColumns={['repeat(2, 1fr)', 'repeat(4, 1fr)']}
      gridGap="8px"
      p={0}
      position="relative"
    >
      <FieldLegend
        as="legend"
        fontSize="16px"
        fontFamily="muliBold"
      >
        Weight:
      </FieldLegend>
      {petWeightOptions.map((weightOption) => (
        <Box
          key={weightOption.value}
          mt="30px"
        >
          <HiddenRadioInput
            type="radio"
            id={weightOption.value.toString()}
            name="weight"
            checked={formik.values[FormFields.PetWeight] === weightOption.value}
            onChange={() => {
              formik.setFieldValue(FormFields.PetWeight, weightOption.value);
            }}
            {...weightOption.value === PetStrings.Weight.Small && {
              'data-testId': DataTestIds.Signup.PetInfoForm2.PetWeightSmall,
            }}
            {...weightOption.value === PetStrings.Weight.Medium && {
              'data-testId': DataTestIds.Signup.PetInfoForm2.PetWeightMedium,
            }}
            {...weightOption.value === PetStrings.Weight.Large && {
              'data-testId': DataTestIds.Signup.PetInfoForm2.PetWeightLarge,
            }}
            {...weightOption.value === PetStrings.Weight.XL && {
              'data-testId': DataTestIds.Signup.PetInfoForm2.PetWeightXL,
            }}
          />
          <FieldLabel
            htmlFor={weightOption.value.toString()}
            display="grid"
            textAlign="center"
            checked={formik.values[FormFields.PetWeight] === weightOption.value}
          >
            {formik.values[FormFields.PetWeight] === weightOption.value && (
              <StyledConfirmedCheckIcon />
            )}

            {WeightIcon[weightOption.value]}
            <Typography
              m={0}
              fontFamily="muliBold"
              color="inherit"
            >
              {weightOption.label}
            </Typography>
            <Typography
              variant="body2"
              m={0}
              fontFamily="muliBold"
              fontSize="12px"
              color={theme.colors.darkGray}
              textTransform="uppercase"
            >
              {weightOption.sizeNameDisplayLabel}
            </Typography>
          </FieldLabel>
        </Box>
      ))}
    </Grid>

    <Divider
      width={1}
    />
    <StepFooter
      primaryCtaText="Next"
      primaryCtaProps={{
        disabled: !formik.isValid || formik.isSubmitting,
        type: 'submit',
        ...{
          'data-testid': DataTestIds.Signup.PetInfoForm2.SubmitBtn,
        },
      }}
    />
  </Box>
);

const StyledConfirmedCheckIcon = styled(CheckConfirmedIcon).attrs<SvgIconProps>({
  fontSize: '40px',
  position: 'absolute',
  top: 0,
  right: -3,
}) <SvgIconProps>``;
